/* eslint-disable react/no-unknown-property */
import type { ReactElement, SyntheticEvent } from 'react';

import Head from 'next/head';
import { useEffect, useRef, useState, useCallback } from 'react';
import styled, { css } from 'styled-components';

import { imageBlurData } from '../../../../utilities/constants/base64Images/imageBlurData';
import { media } from '../../../shared/core/styles';
import { AiWatermark } from '~/components/shared/aiWatermark/AiWatermark';
import { DEFAULT_LARGE_DESKTOP_BREAKPOINT } from '~/utilities/constants';
import { SiteType } from '~/utilities/graphql/codegen';
import { getSiteStyles } from '~/utilities/helpers';
import { removeAiWatermark } from '~/utilities/helpers/shouldShowAiIcon';

export interface ProductImageProperty {
  url: string;
  ariaLabel?: string;
  altText?: string;
  onClick?: () => void;
  profile?: string;
  tabIndex?: number;
  dataImageIndex?: number;
  isAI?: boolean;
}

export interface CarouselImageProperty {
  original: ProductImageProperty;
  lazyLoad?: boolean;
  largeDesktopBreakpoint?: number;
  isFullWidthImage?: boolean;
}

const MAX_IMAGE_HEIGHT = 4000;

const ratioMapping: Map<string, number> = new Map([
  ['1920', 1024],
  ['1440', 760],
  ['1280', 520],
  ['1024', 440],
  ['768', 1024],
  ['429', 767],
  ['428', 428],
]);

const S = {
  Wrapper: styled.div`
    position: relative;
    width: 100%;
    height: 100%;
  `,
  ImageWrapper: styled.div`
    ${({ theme }) => css`
      display: flex;
      justify-content: center;
      position: inherit;
      height: 100%;
      width: 100%;

      picture {
        position: relative;
        display: block;
        height: 100%;
        z-index: auto;
      }

      @media ${media(theme).lessThan('lg')} {
        width: 100%;
      }

      ${getSiteStyles(theme.siteType, {
        [SiteType.Aaf]: css`
          aspect-ratio: 4 / 5;
        `,
      })}
    `}
  `,

  Image: styled.img<{
    $largeDesktopBreakpoint?: number;
  }>`
    ${({ theme, $largeDesktopBreakpoint = DEFAULT_LARGE_DESKTOP_BREAKPOINT }) => css`
      width: auto;
      height: 100%;
      aspect-ratio: 1 / 1;
      transition: opacity 250ms ease-in-out 0s;
      opacity: 0;

      &:focus-visible {
        outline: 2px solid ${theme.colors.border.focused};
        outline-offset: -2px;
      }

      &.isLoaded {
        opacity: 1;
      }

      @media ${media(theme).greaterThan('lg')} {
        &.isLoaded {
          object-fit: cover;
          aspect-ratio: 1 / 1;
          height: 100%;
          width: auto;
        }
      }

      @media (min-width: ${$largeDesktopBreakpoint}px) {
        --img-width-size: calc(
          (
              100vw -
                max(
                  ${theme.variables.product?.details.width.desktop},
                  ${theme.variables.product?.details.width.min.desktop}
                )
            ) / 2
        );

        &.isLoaded {
          width: var(--img-width-size);
          height: var(--img-width-size);
        }
      }

      @media ${media(theme).greaterThan('xl')} {
        .has-open-sideNavigation & {
          --img-width-size: calc(
            (
                100vw -
                  max(
                    ${theme.variables.product?.details.width.desktop},
                    ${theme.variables.product?.details.width.min.desktop}
                  ) + (var(--side-nav-width) * -1)
              ) / 2
          );
        }
      }

      ${getSiteStyles(theme.siteType, {
        [SiteType.Aaf]: css`
          aspect-ratio: 4 / 5;

          @media ${media(theme).greaterThan('lg')} {
            &.isLoaded {
              height: 100%;
              aspect-ratio: 4 / 5;
              object-position: 50% 50%;
              max-width: 100%;
            }
          }
        `,
      })}

      @media (prefers-reduced-motion) {
        transition: none;
      }
    `}
  `,
};

export const ProductImage = ({
  original: {
    url,
    altText = '',
    ariaLabel = '',
    onClick,
    profile,
    tabIndex = 0,
    dataImageIndex,
    isAI,
  },
  lazyLoad,
  largeDesktopBreakpoint = DEFAULT_LARGE_DESKTOP_BREAKPOINT,
  isFullWidthImage,
}: CarouselImageProperty): ReactElement => {
  const imageWrapperRef = useRef<HTMLDivElement>(null);
  const [isLoaded, setIsLoaded] = useState(false);

  const getResponsiveImageUrl = useCallback(
    (width: number) => {
      const smallImageHeight = ratioMapping.get(String(width));

      if (!smallImageHeight) {
        return '';
      }

      const height = isFullWidthImage && width >= 1024 ? smallImageHeight * 2 : smallImageHeight;

      const retinaMultiplier = width >= 1024 ? 2 : 1.5;
      const retinaHeight = Math.min(MAX_IMAGE_HEIGHT, Math.round(height * retinaMultiplier));

      const templateReplace = (val: number): string =>
        removeAiWatermark(
          url?.replace('{{dimensions}}', `h_${val}`).replace(/h_\d+/, `h_${val}`)
        ) ?? '';

      return `${templateReplace(height)}, ${templateReplace(retinaHeight)} 2x`;
    },
    [url, isFullWidthImage]
  );

  useEffect(() => {
    setIsLoaded(false);
  }, [url]);

  useEffect(() => {
    const element = imageWrapperRef.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          [...entry.target.querySelectorAll('source')].forEach((el: HTMLSourceElement) => {
            if (el.dataset.srcset) {
              el.setAttribute('srcset', el.dataset.srcset);
            }
          });
          observer.unobserve(entry.target);
        }
      },
      {
        root: null,
        rootMargin: '0px -1px 0px -1px',
        threshold: 0.01,
      }
    );

    if (element) {
      setIsLoaded(false);
      observer.observe(element);
    }

    return () => {
      if (element) {
        observer.unobserve(element);
      }
    };
  }, []);

  const loadingStateHandler = useCallback((event: SyntheticEvent) => {
    const currentSrc = (event.target as HTMLImageElement)?.currentSrc;

    // only trigger onload when currentSrc is a non-1x1px gif
    if (currentSrc && !/data:image\/gif/.test(currentSrc)) {
      setIsLoaded(true);
    }
  }, []);

  return (
    <S.Wrapper>
      {!lazyLoad && (
        <Head>
          {[...ratioMapping.keys()].map((breakpoint, index, array) => {
            const isFirst = index === 0;
            const isLast = index === array.length - 1;

            let mediaQuery: string;

            if (isFirst) {
              mediaQuery = `(min-width: ${breakpoint}px)`;
            } else if (isLast) {
              mediaQuery = `(max-width: ${breakpoint}px)`;
            } else {
              mediaQuery = `(min-width: ${breakpoint}px) and (max-width: ${
                Number(array[index - 1]) - 1
              }px)`;
            }

            return (
              <link
                key={breakpoint}
                rel="preload"
                as="image"
                imageSrcSet={getResponsiveImageUrl(Number(breakpoint))}
                fetchPriority="high"
                media={mediaQuery}
              />
            );
          })}
        </Head>
      )}
      <S.ImageWrapper ref={imageWrapperRef}>
        <picture>
          {[...ratioMapping.keys()].map((entry: string, index, array) => {
            const srcSetUrl = getResponsiveImageUrl(Number(entry));

            return (
              <source
                key={entry}
                {...(lazyLoad ? { 'data-srcset': srcSetUrl } : { srcSet: srcSetUrl })}
                media={`(${array.length - 1 === index ? 'max' : 'min'}-width: ${entry}px)`}
              />
            );
          })}
          <S.Image
            tabIndex={tabIndex}
            onLoad={loadingStateHandler}
            onError={loadingStateHandler}
            className={isLoaded || !lazyLoad ? 'isLoaded' : ''}
            src={imageBlurData}
            alt={ariaLabel || altText}
            decoding="async"
            onClick={onClick}
            data-profile={profile}
            data-image-index={dataImageIndex}
            fetchPriority={lazyLoad ? 'auto' : 'high'}
            $largeDesktopBreakpoint={largeDesktopBreakpoint}
          />
          <AiWatermark show={isAI} className="ai-icon-container" />
        </picture>
      </S.ImageWrapper>
    </S.Wrapper>
  );
};
